/**
 * record-procedures/runs-api — zadania przycisku SQL na formularzu rekordu (wykonywane w tle).
 *
 * Klient trasy `/api/record-procedures/runs` (osobny od katalogu procedur z `api.ts`: katalog
 * zarządza definicjami w Ustawieniach, tu jest uruchamianie i stan na karcie rekordu):
 *   POST /runs — zlecenie (202 `{ run }`, status `queued`; sam wiersz zadania — bez nazwy procedury i
 *                zlecającego, patrz `StartedRecordRun`); odmowy: 403 `PERMISSION_DENIED`,
 *                404 `CONTRACTOR_NOT_FOUND`, 404 `PROCEDURE_UNAVAILABLE`, 409 `RUN_IN_PROGRESS`
 *                (z aktywnym zadaniem), 400 walidacja.
 *   GET  /runs — stan przycisków rekordu: aktywne zadanie (kolejka / w toku) albo null +
 *                ostatnie zadanie każdej procedury (najnowsze pierwsze, łącznie z aktywnym).
 *
 * Zapis i usuwanie rekordu w trakcie zadania odpowiadają 409 `RECORD_LOCKED_BY_RUN` z tym samym
 * widokiem aktywnego zadania — `extractRunConflict` czyta oba kształty (patrz niżej).
 */

import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

/** Typy kartotek, na których front uruchamia przycisk SQL (12 hostów: 5 kartotek + dokument). */
export type RunnableRecordType =
  | 'contractor'
  | 'contractor_contact'
  | 'employee'
  | 'user'
  | 'product'
  | 'document';

export type RecordRunStatus = 'queued' | 'running' | 'done' | 'failed' | 'interrupted';
export type RecordRunLane = 'fast' | 'slow';

/** Zadanie w odpowiedziach API (daty jako ISO). */
export interface RecordProcedureRun {
  id: string;
  procedureId: string;
  /** Nazwa procedury z katalogu (także usuniętej). */
  procedureName: string;
  recordType: string;
  recordClass: string | null;
  recordId: string;
  companyId: string | null;
  lane: RecordRunLane;
  status: RecordRunStatus;
  timeoutMs: number;
  /** 0 = pierwsze podejście, 1 = jedno automatyczne ponowienie po restarcie serwera. */
  attempts: number;
  /** Wynik procedury (done) albo opis przerwania / wznowienia; null = brak. */
  message: string | null;
  rowCount: number | null;
  /** Gotowy polski tekst błędu (failed); null w pozostałych stanach. */
  error: string | null;
  requestedByUserId: string;
  /** Nazwa wyświetlana zlecającego (gdy brak — e-mail); null, gdy konta nie ma w bazie. */
  requestedByName: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/**
 * Zadanie w odpowiedzi 202 na zlecenie: serwer zwraca tam sam wiersz zadania, bez dołączonych nazw
 * (procedury i zlecającego) — te dochodzą dopiero z odczytu stanu (`GET /runs`), którym strona
 * odświeża się zaraz po zleceniu.
 */
export type StartedRecordRun = Omit<RecordProcedureRun, 'procedureName' | 'requestedByName'>;

/** Stan zadań rekordu (odpowiedź GET). */
export interface RecordRunsState {
  active: RecordProcedureRun | null;
  lastByProcedure: RecordProcedureRun[];
}

export interface RecordRunTarget {
  recordType: RunnableRecordType;
  recordId: string;
  /** Tylko dla dokumentów (klasa dokumentu); kontrahent go nie ma. */
  recordClass?: string;
}

/** Zadania w kolejce lub w toku — blokują rekord. */
export function isActiveRunStatus(status: RecordRunStatus): boolean {
  return status === 'queued' || status === 'running';
}

// ============================================================================
// Calls
// ============================================================================

/** Klucz zapytania o stan zadań rekordu — wspólny dla hooka i unieważnień. */
export function recordRunsKey(
  recordType: string,
  recordId: string | null | undefined,
  recordClass?: string,
): readonly ['record-procedure-runs', string, string | null, string | null] {
  return ['record-procedure-runs', recordType, recordId ?? null, recordClass ?? null] as const;
}

export async function fetchRecordRuns(target: RecordRunTarget): Promise<RecordRunsState> {
  const params = new URLSearchParams({
    recordType: target.recordType,
    recordId: target.recordId,
  });
  if (target.recordClass) params.set('recordClass', target.recordClass);
  const res = await api.get<RecordRunsState>(`/record-procedures/runs?${params.toString()}`);
  return res.data;
}

/**
 * Zleca zadanie procedury na rekordzie. `companyId` (opcjonalne) = firma, w kontekście której idzie
 * zlecenie (nagłówek `X-Company-Id`): potrzebne tuż po utworzeniu rekordu, gdy firma wybrana w formularzu
 * nie musi być aktywną firmą aplikacji (np. tryb „Wszystkie firmy"). Bez niego obowiązuje aktywna firma.
 */
export async function startRecordRun(
  procedureId: string,
  target: RecordRunTarget,
  companyId?: string,
): Promise<StartedRecordRun> {
  const body: { procedureId: string; recordType: string; recordId: string; recordClass?: string } =
    {
      procedureId,
      recordType: target.recordType,
      recordId: target.recordId,
    };
  if (target.recordClass) body.recordClass = target.recordClass;
  const url = '/record-procedures/runs';
  const res = companyId
    ? await api.post<{ run: StartedRecordRun }>(url, body, {
        headers: { 'X-Company-Id': companyId },
      })
    : await api.post<{ run: StartedRecordRun }>(url, body);
  return res.data.run;
}

// ============================================================================
// Errors
// ============================================================================

/** Kody odmów, które front rozróżnia (reszta = ogólny błąd). */
export type RunErrorCode =
  | 'PERMISSION_DENIED'
  | 'PROCEDURE_UNAVAILABLE'
  | 'RUN_IN_PROGRESS'
  | 'RECORD_LOCKED_BY_RUN';

export interface RunConflict {
  code: RunErrorCode;
  /** Aktywne zadanie rekordu dołączone przez serwer (409); null, gdy już się skończyło. */
  activeRun: RecordProcedureRun | null;
  /** Gotowy polski komunikat serwera (jeśli jest). */
  message: string | null;
}

const RUN_ERROR_CODES: readonly RunErrorCode[] = [
  'PERMISSION_DENIED',
  'PROCEDURE_UNAVAILABLE',
  'RUN_IN_PROGRESS',
  'RECORD_LOCKED_BY_RUN',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const RUN_STATUSES: readonly RecordRunStatus[] = [
  'queued',
  'running',
  'done',
  'failed',
  'interrupted',
];

/**
 * Aktywne zadanie z treści błędu 409 — nieufny JSON z sieci. Sprawdza pola, na których opiera się
 * wyświetlanie (id, procedura, status, data zlecenia); niezgodność = `null` (wołający pokaże wtedy
 * ogólny komunikat bez opisu zadania zamiast rzucać wyjątek przy formatowaniu).
 */
function toActiveRun(value: unknown): RecordProcedureRun | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== 'string' || typeof value.procedureId !== 'string') return null;
  if (typeof value.createdAt !== 'string') return null;
  if (!RUN_STATUSES.some((status) => status === value.status)) return null;
  return value as unknown as RecordProcedureRun;
}

/**
 * Błąd odpowiedzi związany z zadaniami przycisku (kod z listy `RunErrorCode`) albo `null` —
 * wtedy wołający pokazuje swój zwykły komunikat. Czyta ten sam kształt z trasy zleceń
 * (`RUN_IN_PROGRESS`, 403/404) i z tras zapisu/usuwania rekordu (`RECORD_LOCKED_BY_RUN`).
 */
export function extractRunConflict(err: unknown): RunConflict | null {
  if (!isAxiosError(err)) return null;
  const data: unknown = err.response?.data;
  if (!isRecord(data)) return null;
  const code = RUN_ERROR_CODES.find((c) => c === data.error);
  if (code === undefined) return null;
  return {
    code,
    activeRun: toActiveRun(data.activeRun),
    message: typeof data.message === 'string' ? data.message : null,
  };
}

// ============================================================================
// Formatting
// ============================================================================

/** Zastępnik czasu, gdy data z serwera jest nieczytelna — komunikat o zadaniu nie rzuca wyjątku. */
const UNKNOWN_TIME = '—';

/**
 * Chwila zadania w czasie przeglądarki, wg języka interfejsu: z dzisiaj — sama godzina i minuta
 * (np. „10:02”); sprzed dzisiaj — krótka data + godzina (np. „31.10, 10:02”, z rokiem, gdy to inny rok niż
 * obecny), żeby stary wynik nie wyglądał na dzisiejszy. Nieczytelna data → „—”. `now` tylko dla testów.
 */
export function formatRunClock(iso: string, locale: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return UNKNOWN_TIME;
  const time: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  // Data z przyszłości (rozjazd zegara przeglądarki z serwerem) liczy się jak dzisiejsza.
  const isTodayOrLater =
    date.getTime() > now.getTime() ||
    (date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate());
  if (isTodayOrLater) return new Intl.DateTimeFormat(locale, time).format(date);
  const dated: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    ...time,
  };
  return new Intl.DateTimeFormat(locale, dated).format(date);
}

/** Od kiedy zadanie jest aktywne: start wykonania, a w kolejce — chwila zlecenia. */
export function runSinceIso(run: RecordProcedureRun): string {
  return run.startedAt ?? run.createdAt;
}

/** Kiedy zadanie się skończyło (stany końcowe); gdy brak znacznika — chwila zlecenia. */
export function runFinishedIso(run: RecordProcedureRun): string {
  return run.finishedAt ?? run.createdAt;
}
